const VariantSwatches = (() => {
  // ---------- Helpers ----------
  function isGid(str) {
    return typeof str === "string" && str.startsWith("gid://");
  }

  function getField(fields, key) {
    const f = fields.find(x => x.key === key);
    return f ? f.value : null;
  }

  async function fetchGraphQL(query) {
    const res = await fetch(`https://${SHOP_DOMAIN}/api/2024-04/graphql.json`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_TOKEN
      },
      body: JSON.stringify({ query })
    });
    const json = await res.json();
    if (json.errors) console.error("GraphQL errors:", json.errors);
    return json.data;
  }

  async function fetchAllGroups() {
    let hasNextPage = true, afterCursor = null, allEdges = [];
    while (hasNextPage) {
      const query = `
        {
          metaobjects(first: 250${afterCursor ? `, after: "${afterCursor}"` : ""}, type: "product_group") {
            edges { cursor node { id fields { key value } } }
            pageInfo { hasNextPage }
          }
        }
      `;
      const data = await fetchGraphQL(query);
      if (!data || !data.metaobjects) break;
      allEdges = allEdges.concat(data.metaobjects.edges);
      hasNextPage = data.metaobjects.pageInfo.hasNextPage;
      if (hasNextPage) afterCursor = data.metaobjects.edges.slice(-1)[0].cursor;
    }
    return allEdges.map(e => e.node);
  }

  async function fetchNestedMetaobjects(gids) {
    if (gids.length === 0) return {};
    const idsString = gids.map(gid => `"${gid}"`).join(", ");
    const query = `
      query {
        nodes(ids: [${idsString}]) {
          ... on Metaobject {
            id
            fields { key value reference { ... on MediaImage { image { url } } } }
          }
        }
      }
    `;
    const data = await fetchGraphQL(query);
    const map = {};
    data?.nodes?.forEach(node => {
      if (!node) return;
      const fieldsObj = {};
      node.fields.forEach(f => {
        fieldsObj[f.key] = f.reference?.image?.url || f.value;
      });
      map[node.id] = fieldsObj;
    });
    return map;
  }

  // ---------- Fetch products with price ----------
  async function fetchProductHandlesByIds(productIds) {
    if (productIds.length === 0) return {};
    const idsString = productIds.map(id => `"gid://shopify/Product/${id}"`).join(", ");
    const query = `
      query {
        nodes(ids: [${idsString}]) {
          ... on Product {
            id
            handle
            priceRange { minVariantPrice { amount currencyCode } }
            images(first: 2) { edges { node { url } } }
          }
        }
      }
    `;
    const data = await fetchGraphQL(query);
    const handles = {};
    data?.nodes?.forEach(n => {
      if (!n || !n.id || !n.handle) return;
      const id = n.id.split("/").pop();
      handles[id] = {
        handle: n.handle,
        price: n.priceRange?.minVariantPrice?.amount || "0.00",
        currency: n.priceRange?.minVariantPrice?.currencyCode || "USD",
        image1: n.images?.edges?.[0]?.node?.url || "",
        image2: n.images?.edges?.[1]?.node?.url || ""
      };
    });
    return handles;
  }

  function createColorDiv(color, isActive) {
    const div = document.createElement("div");
    div.className = "variant-color-option";
    if (isActive) div.classList.add("variant-active");
    div.style.background = color || "#ccc";
    return div;
  }

  function createImageDiv(imageUrl, isActive) {
    const div = document.createElement("div");
    div.className = "variant-swatch-image";
    if (isActive) div.classList.add("variant-active");
    div.style.backgroundImage = `url(${imageUrl})`;
    div.style.backgroundSize = "cover";
    div.style.backgroundPosition = "center";
    div.style.borderRadius = "50%";
    div.style.border = "1px solid #ccc";
    return div;
  }

  // ---------- Core render ----------
  async function renderGroup(container, metaobjects, nestedMetaMap, groupTitle, currentHandle, filterKey, filterValue, swatchKey) {
    const filtered = metaobjects.filter(obj => {
      if (getField(obj.fields, "parent_sku") !== groupTitle) return false;
      const val = getField(obj.fields, filterKey);
      if (isGid(val)) {
        const nested = nestedMetaMap[val];
        if (!nested) return false;
        const name = nested[filterKey + "_name"] || nested.title || "";
        return name.toLowerCase() === filterValue.toLowerCase();
      } else {
        return val?.toLowerCase() === filterValue.toLowerCase();
      }
    });

    const productIds = filtered.map(obj => getField(obj.fields, "product")?.split("/").pop()).filter(Boolean);
    if (!productIds.length) return;

    const handlesMap = await fetchProductHandlesByIds(productIds);

    container.innerHTML = "";

    const maxVisible = 4;
    const visibleItems = filtered.slice(0, maxVisible);
    const hiddenCount = filtered.length - maxVisible;

    visibleItems.forEach(obj => {
      const productId = getField(obj.fields, "product")?.split("/").pop();
      const productData = handlesMap[productId];
      if (!productData) return;
      const swatchGid = getField(obj.fields, swatchKey);
      const swatchData = nestedMetaMap[swatchGid];
      const swatchName = swatchData?.[`${swatchKey}_name`] || swatchData?.title || "Swatch";
      const swatchDiv = (swatchData?.swatch_image?.startsWith("http"))
        ? createImageDiv(swatchData.swatch_image, productData.handle === currentHandle)
        : createColorDiv(swatchData?.swatch || "#ccc", productData.handle === currentHandle);

      swatchDiv.title = swatchName;
      const link = document.createElement("a");
      link.href = `/products/${productData.handle}`;
      link.dataset.image1 = productData.image1;
      link.dataset.image2 = productData.image2;
      link.dataset.price = productData.price; // added price
      link.appendChild(swatchDiv);
      container.appendChild(link);
    });

    if (hiddenCount > 0) {
      const moreDiv = document.createElement("div");
      moreDiv.className = "variant-swatch-more";
      moreDiv.textContent = `+${hiddenCount}`;
      moreDiv.style.cssText = "display:inline-flex;align-items:center;justify-content:center;border:1px solid #ccc;border-radius:50%;width:28px;height:28px;font-size:12px;background:#f4f4f4;cursor:default;";
      moreDiv.title = `${hiddenCount} more option${hiddenCount > 1 ? 's' : ''}`;
      container.appendChild(moreDiv);
    }
  }

  async function processCard(container, metaobjects, nestedMeta) {
    const groupTitle = container.dataset.groupTitle;
    const currentHandle = container.dataset.productHandle;
    const mainMetal = container.dataset.variantMetal;
    const mainOption = container.dataset.variantOption;
    const mainGemstone = container.dataset.variantGemstone;

    if (container.classList.contains("metal-option-type")) {
      container.innerHTML = `<div class="variant-options variant-metal-options"></div><div class="variant-options variant-option-options"></div>`;
      await renderGroup(container.querySelector(".variant-metal-options"), metaobjects, nestedMeta, groupTitle, currentHandle, "option", mainOption, "metal");
      await renderGroup(container.querySelector(".variant-option-options"), metaobjects, nestedMeta, groupTitle, currentHandle, "metal", mainMetal, "option");
    } else if (container.classList.contains("metal-gemstone-type")) {
      container.innerHTML = `<div class="variant-options variant-metal-options"></div><div class="variant-options variant-gemstone-options"></div>`;
      await renderGroup(container.querySelector(".variant-metal-options"), metaobjects, nestedMeta, groupTitle, currentHandle, "gemstone", mainGemstone, "metal");
      await renderGroup(container.querySelector(".variant-gemstone-options"), metaobjects, nestedMeta, groupTitle, currentHandle, "metal", mainMetal, "gemstone");
    } else if (container.classList.contains("metal-only-type")) {
      container.innerHTML = `<div class="variant-options variant-metal-options"></div>`;
      await renderGroup(container.querySelector(".variant-metal-options"), metaobjects, nestedMeta, groupTitle, currentHandle, "parent_sku", groupTitle, "metal");
    }

    // Update lowest price if option is "Pair"
    updateLowestPriceIfPair(container.closest("product-card"));
  }

  // ---------- Update lowest price function ----------
  function updateLowestPriceIfPair(card) {
    const wrapper = card.querySelector(".variant-swatch-wrapper.metal-option-type");
    if (!wrapper) return;

    const optionType = wrapper.dataset.variantOption;
    if (optionType.toLowerCase() !== "pair") return; // only for "Pair"

    const discount = parseFloat(wrapper.dataset.discount) || 0;

    const optionLinks = wrapper.querySelectorAll(".variant-option-options a");
    if (!optionLinks.length) return;

    const prices = Array.from(optionLinks)
      .map(a => parseFloat(a.dataset.price))
      .filter(p => !isNaN(p));
    if (!prices.length) return;

    let lowestPrice = Math.min(...prices);
    lowestPrice *= 2; // multiply by 2 for "Pair"
    const finalPrice = lowestPrice * (1 - discount / 100);

    const salePriceEl = card.querySelector("sale-price");
    if (salePriceEl) salePriceEl.textContent = `$${finalPrice.toFixed(2)}`;
    salePriceEl.classList.add("updated-sale-price"); // 👈 added here
  }

  // ---------- Public function ----------
  async function init(scope = document) {
    const containers = scope.querySelectorAll(".variant-swatch-wrapper");
    if (!containers.length) return;

    const metaobjects = await fetchAllGroups();
    const allGIDs = [...new Set(metaobjects.flatMap(obj => ["metal","gemstone","option"].map(k => getField(obj.fields, k)).filter(isGid)))];
    const nestedMeta = await fetchNestedMetaobjects(allGIDs);

    for (const container of containers) {
      processCard(container, metaobjects, nestedMeta);
    }
  }

  // ---------- Swatch click handler ----------
  document.body.addEventListener("click", (e) => {
    const link = e.target.closest(".variant-options a");
    if (!link) return;

    e.preventDefault();

    const card = link.closest("product-card");
    if (!card) return;

    const primaryImg = card.querySelector(".product-card__image--primary");
    const secondaryImg = card.querySelector(".product-card__image--secondary");
    const main_a = card.querySelector(".product-card__figure a");

    if (main_a) main_a.href = link.href;

    if (primaryImg && link.dataset.image1) {
      primaryImg.src = link.dataset.image1;
      primaryImg.srcset = generateSrcset(link.dataset.image1);
      primaryImg.sizes = primaryImg.getAttribute("sizes") || "100vw";
    }

    if (secondaryImg && link.dataset.image2) {
      secondaryImg.src = link.dataset.image2;
      secondaryImg.srcset = generateSrcset(link.dataset.image2);
      secondaryImg.sizes = secondaryImg.getAttribute("sizes") || "100vw";
    }

    const wrapper = link.closest(".variant-options");
    if (wrapper) {
      wrapper.querySelectorAll("a").forEach(a => {
        a.classList.remove("variant-active");
        a.querySelector("div")?.classList.remove("variant-active");
      });
    }
    link.classList.add("variant-active");
    link.querySelector("div")?.classList.add("variant-active");

    const title = link.querySelector("div")?.title || "";
    const span = card.querySelector(".optiitl span");
    if (span && title) span.textContent = title;
  });

  function generateSrcset(baseUrl) {
    const widths = [200, 300, 400, 500, 600, 700, 800, 1000, 1200];
    return widths.map(w => `${baseUrl}&width=${w} ${w}w`).join(", ");
  }

  return { init };
})();

document.addEventListener("DOMContentLoaded", () => VariantSwatches.init());